import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Tabs, Typography } from '@material-ui/core';

import { useTranslation } from 'react-i18next';
import sortBy from 'lodash.sortby';
import { usePrevious } from 'hooks';
import useBankTransactions from 'hooks/useBankTransactions';
import { BankTransactionsQuery } from 'store/applications/types';
import { StyledTab } from 'components/StyledTab';
import { TransactionsTable } from './components/TransactionsTable';
import useDetailsStyles from '../LeadDetails/LeadDetails.styles';
import useStyles, { useTabsStyles } from './BankData.styles';
import { TransactionsChart } from './components/TransactionsChart';

interface BankDataProps {
  companyId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bankProviders: Record<any, any[]>;
}

const BankData: FC<BankDataProps> = ({ companyId, bankProviders }) => {
  const { loading, error, transactions, getBankTransactions, total } = useBankTransactions();
  const detailsClasses = useDetailsStyles();
  const tabsClasses = useTabsStyles();
  const classes = useStyles();
  const [activePage, setActivePage] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const prevActivePage = usePrevious(activePage);
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const connectedProvidersList = Object.keys(bankProviders) as any[];

  const connectedProviders = Object.entries(bankProviders).flatMap(([bank_name, accounts]) =>
    accounts.map((account) => `${bank_name} - ${account.account_id}`),
  );

  const selectedProvider = connectedProvidersList[activeTab];

  const selectProviderId = useMemo(() => {
    return bankProviders[selectedProvider]?.[0]?.id;
  }, [bankProviders, selectedProvider]);

  const transactionsQuery: BankTransactionsQuery = useMemo(
    () => ({
      per_page: 10,
      page: activePage + 1,
      companyAccountId: selectProviderId,
    }),
    [activePage, selectProviderId],
  );

  const handleChange = (event: React.ChangeEvent<object>, newValue: number) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (prevActivePage !== undefined && prevActivePage !== activePage) {
      getBankTransactions(companyId, transactionsQuery);
    }
  }, [activePage, companyId, getBankTransactions, prevActivePage, transactionsQuery]);

  useEffect(() => {
    getBankTransactions(companyId, transactionsQuery);
  }, [companyId, transactionsQuery, getBankTransactions, selectedProvider]);

  return (
    <Box className={classes.container}>
      <Typography className={detailsClasses.sectionTitle}>{t('pages.lead.sections.bankData.title')}</Typography>

      <Tabs
        value={activeTab}
        onChange={handleChange}
        classes={tabsClasses}
        variant="fullWidth"
        scrollButtons="on"
        indicatorColor="primary"
        textColor="primary"
      >
        {connectedProviders.map((provider, index) => (
          <StyledTab key={provider} label={provider} value={index} />
        ))}
      </Tabs>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TransactionsChart
            loading={loading}
            error={!!error}
            transactions={sortBy(transactions, 'booking_date_time')}
          />
        </Grid>

        <Grid item xs={12}>
          <Box marginTop="10px" marginBottom="20px">
            <Typography className={detailsClasses.sectionTitle}>
              {t('pages.lead.sections.bankData.transactionsTable.title')}
            </Typography>
          </Box>
          <TransactionsTable
            transactions={transactions}
            total={total}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BankData;
